/**
 * BRAND CLAIM PAGE
 * 
 * Allows brand representatives to claim their brand page
 * Supports multiple verification methods:
 * - Domain email verification
 * - Website code verification
 * - Social media verification
 * - Manual review request
 */

import { useState, useEffect, type ElementType } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AppIcons } from '../../constants/icons';
import { getCurrentUser } from '../../firebase/auth';
import { getBrandBySlug, submitBrandClaim } from '../../services/brandService';
import type { Brand, VerificationMethod } from '../../types/brand';

export default function BrandClaimPage() {
  const { slug } = useParams<{ slug: string }>();

  const [brand, setBrand] = useState<Brand | null>(null);
  const [loading, setLoading] = useState(true);
  // Split load-time errors (page swaps to error state) from submit-time
  // errors (inline banner, preserves form). Previously a single `error`
  // caused any submit failure to nuke the user's typed input by hitting
  // the top-level error guard.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Form state
  const [step, setStep] = useState<'select' | 'verify' | 'submitted'>('select');
  const [method, setMethod] = useState<VerificationMethod | null>(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [message, setMessage] = useState('');
  const [socialHandle, setSocialHandle] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);

  const currentUser = getCurrentUser();

  // Generate verification code
  useEffect(() => {
    setVerificationCode(
      'PATT-' + Math.random().toString(36).substring(2, 8).toUpperCase()
    );
  }, []);

  // Load brand
  useEffect(() => {
    async function loadBrand() {
      if (!slug) return;
      
      setLoading(true);
      try {
        const brandData = await getBrandBySlug(slug);

        if (!brandData) {
          setLoadError('Brand not found');
          return;
        }

        if (brandData.status === 'verified') {
          setLoadError('This brand has already been verified');
          return;
        }

        if (brandData.status === 'claimed') {
          setLoadError('This brand is already pending verification');
          return;
        }

        setBrand(brandData);
      } catch (err) {
        console.error('Error loading brand:', err);
        setLoadError('Failed to load brand');
      } finally {
        setLoading(false);
      }
    }
    
    loadBrand();
  }, [slug]);

  // Copy verification code
  const copyCode = async () => {
    await navigator.clipboard.writeText(verificationCode);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 2000);
  };

  // Submit claim
  const handleSubmit = async () => {
    if (!currentUser || !brand || !method) return;
    
    setSubmitting(true);
    setSubmitError(null);

    try {
      await submitBrandClaim(
        brand.id,
        currentUser.uid,
        currentUser.email || email,
        currentUser.displayName || 'Brand Representative',
        method,
        {
          email: method === 'domain-email' ? email : null,
          socialHandle: method === 'social-media' ? socialHandle : null,
          verificationCode: ['social-media', 'website-code'].includes(method) ? verificationCode : null,
          role: method === 'manual-review' ? role : null,
          notes: message || null,
        }
      );
      
      setStep('submitted');
    } catch (err: any) {
      console.error('Error submitting claim:', err);
      setSubmitError(err.message || 'Failed to submit claim');
    } finally {
      setSubmitting(false);
    }
  };

  // Verification method cards
  const VerificationMethodCard = ({
    methodType,
    icon: Icon,
    title,
    description,
    recommended
  }: {
    methodType: VerificationMethod;
    icon: ElementType;
    title: string;
    description: string;
    recommended?: boolean;
  }) => (
    <button
      onClick={() => {
        setMethod(methodType);
        setStep('verify');
      }}
      className={`relative text-left p-4 border-2 rounded-xl transition-all hover:shadow-md ${
        method === methodType
          ? 'border-purple-500 bg-purple-600/30'
          : 'border-slate-600 hover:border-purple-500/50 bg-slate-700/30'
      }`}
    >
      {recommended && (
        <span className="absolute -top-2 right-4 px-2 py-0.5 bg-green-500 text-white text-xs font-medium rounded-full">
          Recommended
        </span>
      )}
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${method === methodType ? 'bg-purple-600/50' : 'bg-slate-600'}`}>
          <Icon className={`w-5 h-5 ${method === methodType ? 'text-purple-300' : 'text-glass-secondary'}`} />
        </div>
        <div className="flex-grow">
          <h3 className="font-medium text-glass">{title}</h3>
          <p className="text-sm text-glass-muted mt-1">{description}</p>
        </div>
        <AppIcons.DropdownClosed className="w-5 h-5 text-glass-muted self-center" />
      </div>
    </button>
  );

  if (!currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="glass-card p-6 max-w-md w-full text-center">
          <AppIcons.BrandPending className="w-12 h-12 text-glass-muted mx-auto mb-4" />
          <h2 className="text-xl font-bold text-glass mb-2">Sign In Required</h2>
          <p className="text-glass-secondary mb-4">
            You need to be signed in to claim a brand page.
          </p>
          <Link
            to="/login"
            className="inline-block px-6 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700"
          >
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent"></div>
      </div>
    );
  }

  if (loadError || !brand) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="glass-card p-6 max-w-md w-full text-center">
          <AppIcons.Error className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-glass mb-2">Cannot Claim Brand</h2>
          <p className="text-glass-secondary mb-4">{loadError || 'Brand not found'}</p>
          <Link
            to="/brands"
            className="inline-block px-6 py-2 bg-slate-600 text-glass-secondary rounded-lg font-medium hover:bg-slate-500"
          >
            Browse Brands
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-2xl mx-auto px-4">
        {/* Back link */}
        <Link
          to={`/brands/${slug}`}
          className="inline-flex items-center gap-1 text-black hover:underline mb-6"
        >
          <AppIcons.Back className="w-4 h-4" />
          Back to {brand.name}
        </Link>

        {/* Header */}
        <div className="glass-card p-6 mb-6">
          <div className="flex items-center gap-4 mb-4">
            {brand.logo ? (
              <img
                src={brand.logo}
                alt={brand.name}
                className="w-16 h-16 rounded-xl object-cover border-2 border-slate-600"
              />
            ) : (
              <div className="w-16 h-16 rounded-xl bg-slate-700 flex items-center justify-center border-2 border-slate-600">
                <span className="text-2xl font-bold text-glass">
                  {brand.name.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold text-glass">Claim {brand.name}</h1>
              <p className="text-glass-secondary">Verify your ownership to manage this brand page</p>
            </div>
          </div>

          {/* Progress indicator */}
          <div className="flex items-center gap-2">
            <div className={`flex-1 h-1 rounded-full ${step !== 'select' ? 'bg-purple-500' : 'bg-slate-600'}`}></div>
            <div className={`flex-1 h-1 rounded-full ${step === 'submitted' ? 'bg-purple-500' : 'bg-slate-600'}`}></div>
          </div>
        </div>

        {/* Step: Select verification method */}
        {step === 'select' && (
          <div className="glass-card p-6">
            <h2 className="text-lg font-bold text-glass mb-4">
              How would you like to verify ownership?
            </h2>

            <div className="space-y-3">
              <VerificationMethodCard
                methodType="domain-email"
                icon={AppIcons.MailIcon}
                title="Domain Email"
                description={`Send verification to an email @${brand.website?.replace(/https?:\/\/(www\.)?/, '').split('/')[0] || 'yourbrand.com'}`}
                recommended
              />

              <VerificationMethodCard
                methodType="website-code"
                icon={AppIcons.Website}
                title="Website Code"
                description="Add a verification code to your website"
              />

              <VerificationMethodCard
                methodType="social-media"
                icon={AppIcons.InstagramIcon}
                title="Social Media"
                description="Post a verification code from your official account"
              />

              <VerificationMethodCard
                methodType="manual-review"
                icon={AppIcons.BrandPending}
                title="Manual Review"
                description="Submit proof for manual verification (takes longer)"
              />
            </div>
          </div>
        )}

        {/* Step: Verification details */}
        {step === 'verify' && method && (
          <div className="glass-card p-6">
            <button
              onClick={() => setStep('select')}
              className="text-sm text-purple-400 hover:text-purple-300 mb-4"
            >
              ← Change verification method
            </button>

            {/* Domain Email Verification */}
            {method === 'domain-email' && (
              <>
                <h2 className="text-lg font-bold text-glass mb-4">
                  Verify with Domain Email
                </h2>
                <p className="text-glass-secondary mb-4">
                  Enter your email address at your brand's domain. We'll send you a verification link.
                </p>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-glass-secondary mb-1">
                    Brand Email Address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={`you@${brand.website?.replace(/https?:\/\/(www\.)?/, '').split('/')[0] || 'yourbrand.com'}`}
                    className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-glass placeholder-slate-400 focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </>
            )}

            {/* Website Code Verification */}
            {method === 'website-code' && (
              <>
                <h2 className="text-lg font-bold text-glass mb-4">
                  Verify with Website Code
                </h2>
                <p className="text-glass-secondary mb-4">
                  Add this code somewhere on your website (e.g., in a meta tag or visible text).
                </p>
                <div className="flex items-center gap-2 p-3 bg-slate-600/50 rounded-lg mb-4">
                  <code className="flex-grow font-mono text-sm text-glass">{verificationCode}</code>
                  <button
                    onClick={copyCode}
                    className="p-2 hover:bg-slate-500/50 rounded-lg transition-colors"
                    title="Copy code"
                  >
                    {codeCopied ? (
                      <AppIcons.Confirm className="w-5 h-5 text-green-400" />
                    ) : (
                      <AppIcons.CopyIcon className="w-5 h-5 text-glass-muted" />
                    )}
                  </button>
                </div>
                <p className="text-sm text-glass-muted mb-4">
                  Add this code, then submit. We'll verify within 24-48 hours.
                </p>
              </>
            )}

            {/* Social Media Verification */}
            {method === 'social-media' && (
              <>
                <h2 className="text-lg font-bold text-glass mb-4">
                  Verify with Social Media
                </h2>
                <p className="text-glass-secondary mb-4">
                  Post this verification code from your official Instagram, then share the link.
                </p>
                <div className="flex items-center gap-2 p-3 bg-slate-600/50 rounded-lg mb-4">
                  <code className="flex-grow font-mono text-sm text-glass">{verificationCode}</code>
                  <button
                    onClick={copyCode}
                    className="p-2 hover:bg-slate-500/50 rounded-lg transition-colors"
                    title="Copy code"
                  >
                    {codeCopied ? (
                      <AppIcons.Confirm className="w-5 h-5 text-green-400" />
                    ) : (
                      <AppIcons.CopyIcon className="w-5 h-5 text-glass-muted" />
                    )}
                  </button>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-glass-secondary mb-1">
                    Instagram Handle
                  </label>
                  <div className="flex">
                    <span className="px-3 py-2 bg-slate-600 border border-r-0 border-slate-600 rounded-l-lg text-glass-muted">
                      @
                    </span>
                    <input
                      type="text"
                      value={socialHandle}
                      onChange={(e) => setSocialHandle(e.target.value.replace('@', ''))}
                      placeholder="yourbrand"
                      className="flex-grow px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-r-lg text-glass placeholder-slate-400 focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Manual Review */}
            {method === 'manual-review' && (
              <>
                <h2 className="text-lg font-bold text-glass mb-4">
                  Request Manual Review
                </h2>
                <p className="text-glass-secondary mb-4">
                  Tell us about your connection to this brand. Include any links that prove your relationship.
                </p>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-glass-secondary mb-1">
                    Your Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-glass placeholder-slate-400 focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-glass-secondary mb-1">
                    Your Role
                  </label>
                  <input
                    type="text"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    placeholder="e.g., Owner, Marketing Manager"
                    className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-glass placeholder-slate-400 focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </>
            )}

            {/* Common: Message field */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-glass-secondary mb-1">
                Additional Information (optional)
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Any additional details to help verify your claim..."
                rows={3}
                className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-glass placeholder-slate-400 focus:ring-2 focus:ring-purple-500 resize-none"
              />
            </div>

            {/* Error */}
            {submitError && (
              <div className="flex items-center gap-2 p-3 mb-4 bg-red-600/30 border border-red-500/50 rounded-lg text-red-300">
                <AppIcons.Error className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{submitError}</span>
              </div>
            )}

            {/* Submit button */}
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className={`w-full py-3 rounded-lg font-medium transition-colors ${
                submitting
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : 'bg-purple-600 text-white hover:bg-purple-700'
              }`}
            >
              {submitting ? 'Submitting...' : 'Submit Verification Request'}
            </button>
          </div>
        )}

        {/* Step: Submitted */}
        {step === 'submitted' && (
          <div className="glass-card p-8 text-center">
            <div className="w-16 h-16 bg-green-600/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <AppIcons.Confirm className="w-8 h-8 text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-glass mb-2">
              Claim Submitted!
            </h2>
            <p className="text-glass-secondary mb-6">
              We'll review your request and get back to you within 24-48 hours.
              You'll receive an email at {currentUser.email} once verified.
            </p>
            <Link
              to={`/brands/${slug}`}
              className="inline-block px-6 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700"
            >
              Return to Brand Page
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
